import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { history } from '../_helpers';
import authService from '../services/authService';

// create slice

const name = 'auth';
const initialState = createInitialState();
const reducers = createReducers();
const extraActions = createExtraActions();
const extraReducers = createExtraReducers();
const slice = createSlice({ name, initialState, reducers, extraReducers });

// exports

export const authActions = { ...slice.actions, ...extraActions };

// implementation

function createInitialState() {
    const user = authService.getCurrentUser();
    return {
        // initialize state from auth service
        user: user,
        error: null,
        loading: false
    }
}

function createReducers() {
    return {
        logout,
        clearError
    };

    function logout(state) {
        state.user = null;
        state.error = null;
        authService.clearAuthData();
        history.navigate('/login');
    }

    function clearError(state) {
        state.error = null;
    }
}

function createExtraActions() {
    return {
        login: login(),
        register: register(),
        logout: logout(),
        logoutAll: logoutAll(),
        refreshToken: refreshToken()
    };

    function login() {
        return createAsyncThunk(
            `${name}/user/login`,
            async ({ email, password, deviceName }, { rejectWithValue }) => {
                try {
                    const userData = await authService.login(email, password, deviceName);
                    return userData;
                } catch (error) {
                    return rejectWithValue(error.message);
                }
            }
        );
    }

    function register() {
        return createAsyncThunk(
            `${name}/user/register`,
            async ({ name, email, password }, { rejectWithValue }) => {
                try {
                    const userData = await authService.register(name, email, password);
                    return userData;
                } catch (error) {
                    return rejectWithValue(error.message);
                }
            }
        );
    }

    function logout() {
        return createAsyncThunk(
            `${name}/user/logout`,
            async (_, { rejectWithValue }) => {
                try {
                    await authService.logout();
                    return;
                } catch (error) {
                    return rejectWithValue(error.message);
                }
            }
        );
    }

    function logoutAll() {
        return createAsyncThunk(
            `${name}/user/logoutAll`,
            async (_, { rejectWithValue }) => {
                try {
                    await authService.logoutAll();
                    return;
                } catch (error) {
                    return rejectWithValue(error.message);
                }
            }
        );
    }

    function refreshToken() {
        return createAsyncThunk(
            `${name}/user/refreshToken`,
            async (_, { rejectWithValue }) => {
                try {
                    const accessToken = await authService.refreshAccessToken();
                    return { accessToken };
                } catch (error) {
                    return rejectWithValue(error.message);
                }
            }
        );
    }
}

function createExtraReducers() {
    return {
        ...login(),
        ...register(),
        ...logout(),
        ...logoutAll(),
        ...refreshToken()
    };

    function login() {
        var { pending, fulfilled, rejected } = extraActions.login;
        return {
            [pending]: (state) => {
                state.error = null;
                state.loading = true;
            },
            [fulfilled]: (state, action) => {
                const user = action.payload;
                state.user = user;
                state.loading = false;
                state.error = null;
            },
            [rejected]: (state, action) => {
                state.error = action.payload;
                state.loading = false;
                state.user = null;
            }
        };
    }

    function register() {
        var { pending, fulfilled, rejected } = extraActions.register;
        return {
            [pending]: (state) => {
                state.error = null;
                state.loading = true;
            },
            [fulfilled]: (state, action) => {
                state.loading = false;
                state.error = null;
                // After successful registration, user should login
            },
            [rejected]: (state, action) => {
                state.error = action.payload;
                state.loading = false;
            }
        };
    }

    function logout() {
        var { pending, fulfilled, rejected } = extraActions.logout;
        return {
            [pending]: (state) => {
                state.loading = true;
            },
            [fulfilled]: (state) => {
                state.user = null;
                state.loading = false;
                state.error = null;
            },
            [rejected]: (state, action) => {
                state.user = null; // Clear user even if logout fails
                state.loading = false;
                state.error = action.payload;
            }
        };
    }

    function logoutAll() {
        var { pending, fulfilled, rejected } = extraActions.logoutAll;
        return {
            [pending]: (state) => {
                state.loading = true;
            },
            [fulfilled]: (state) => {
                state.user = null;
                state.loading = false;
                state.error = null;
            },
            [rejected]: (state, action) => {
                state.user = null; // Clear user even if logout fails
                state.loading = false;
                state.error = action.payload;
            }
        };
    }

    function refreshToken() {
        var { pending, fulfilled, rejected } = extraActions.refreshToken;
        return {
            [pending]: (state) => {
                // Don't show loading for background token refresh
            },
            [fulfilled]: (state, action) => {
                // Update user with new token
                if (state.user) {
                    state.user.accessToken = action.payload.accessToken;
                }
            },
            [rejected]: (state, action) => {
                // If refresh fails, user needs to login again
                state.user = null;
                state.error = 'Session expired. Please login again.';
            }
        };
    }
}

export const authSlice = slice;
export default slice.reducer;
